import {
  Box,
  Button,
  ChakraProps,
  Flex,
  Icon,
  List,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, {
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from "react";
import SVG from "react-inlinesvg";
import { useTranslation } from "react-i18next";

import AppSelectOption from "@/features/line-sheet-sets/app-select-option";

type AppSelectSizeType = "medium" | "small";

export type TrendingPeriodValue =
  | "7 days"
  | "1 month"
  | "3 months"
  | "6 months"
  | "1 year";

/** 기존 AppSelect의 사이즈 패딩 */
function calcSizeStyle(size: AppSelectSizeType): ChakraProps {
  if (size === "medium") return { p: "4px 12px" };
  return { p: "4px 8px" };
}

/** 기존 AppSelect의 variant="normal" 스타일 계산 */
function calcVariantStyle(
  borderWidth: string,
  isDisabled: boolean,
  isActive: boolean,
  isHovered: boolean,
  readOnly: boolean
): ChakraProps {
  const defaultStyle: ChakraProps = {
    borderRadius: "4px",
    borderWidth,
    borderStyle: "solid",
    _hover: {},
  };

  let customStyle: ChakraProps = {};

  if (isDisabled) {
    customStyle = {
      borderColor: "#8F8F8C",
      backgroundColor: "#EFEFED",
      color: "#6F6F6C",
      cursor: "default",
    };
  } else if (isActive) {
    customStyle = {
      borderColor: "#1272EF",
      backgroundColor: "#FFF",
      color: "inherit",
    };
  } else if (isHovered) {
    customStyle = {
      borderColor: "#444440",
      backgroundColor: "#FFF",
      color: "#444440",
      cursor: readOnly ? "default" : "pointer",
    };
  } else if (readOnly) {
    customStyle = {
      borderColor: "#6F6F6C",
      backgroundColor: "#FFF",
      color: "#6F6F6C",
      cursor: "default",
    };
  } else {
    customStyle = {
      borderColor: "#8F8F8C",
      backgroundColor: "#FFF",
      color: "#444440",
    };
  }

  return { ...defaultStyle, ...customStyle };
}

export interface OrderDateSelectProps {
  /** 버튼에 표시될 기본 이름 (기존 AppSelect의 name 역할) */
  name?: string; // default: "기간"
  /** Popover Header 타이틀 */
  title?: string; // default: "기간 선택"

  /** ✅ period 값을 그대로 들고감 */
  value?: TrendingPeriodValue;
  /** ✅ period 값을 그대로 전달 */
  onSelect: (period?: TrendingPeriodValue) => void;

  /** ✅ 필요하면 clear 지원 */
  isClearable?: boolean;

  minWidth?: string;
  width?: string;
  height?: string;

  isDisabled?: boolean;
  isReadOnly?: boolean;

  size?: AppSelectSizeType;
  borderWidth?: string;

  dropdownWidth?: string;
  popoverStrategy?: "fixed" | "absolute";
}

export default function OrderDateSelect({
  name = "오더 일자",
  title = "기간 선택",
  value,
  onSelect,
  isClearable = false,
  minWidth,
  width,
  height = "28px",
  isDisabled = false,
  isReadOnly = false,
  size = "medium",
  borderWidth = "0.5px",
  dropdownWidth,
  popoverStrategy = "absolute",
}: OrderDateSelectProps) {
  const [isHover, setIsHover] = useState(false);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { t } = useTranslation();

  // ✅ 변경된 옵션 리스트(요청 그대로)
  const trendingBrandsPeriodOptionList = useMemo(
    () =>
      [
        { name: t("common:period.7days"), value: "7 days" },
        { name: t("common:period.1month"), value: "1 month" },
        { name: t("common:period.3month"), value: "3 months" },
        { name: t("common:period.6month"), value: "6 months" },
        { name: t("common:period.1year"), value: "1 year" },
      ] as const,
    [t]
  );

  // AppSelectOption 형태로 맞춤
  const appSelectOptions = useMemo(
    () =>
      trendingBrandsPeriodOptionList.map((o) => ({
        name: o.name,
        value: o.value as TrendingPeriodValue,
      })),
    [trendingBrandsPeriodOptionList]
  );

  const isActive = value !== undefined;

  const customStyle = useMemo(
    () => ({
      ...calcVariantStyle(
        borderWidth,
        isDisabled,
        isActive,
        isHover,
        isReadOnly
      ),
      ...calcSizeStyle(size),
    }),
    [borderWidth, isDisabled, isActive, isHover, isReadOnly, size]
  );

  const handlePopoverClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleToggle: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.stopPropagation();
      if (isDisabled || isReadOnly) return;
      if (appSelectOptions.length > 0) onToggle();
    },
    [isDisabled, isReadOnly, onToggle, appSelectOptions.length]
  );

  // ✅ active 체크: 문자열 비교로 안정화
  const calcIsActive = useCallback(
    (optValue: TrendingPeriodValue) => value === optValue,
    [value]
  );

  const handleClick = useCallback(
    (opt: { name: string; value: TrendingPeriodValue }) => {
      onSelect(opt.value);
      handlePopoverClose();
    },
    [handlePopoverClose, onSelect]
  );

  const triggerComponent = useMemo(() => {
    if (value) {
      const selected = trendingBrandsPeriodOptionList.find(
        (o) => o.value === value
      );
      // 스샷처럼 "기간 : 최근 7일" 형태
      return <Text>{`${name} : ${selected?.name ?? value}`}</Text>;
    }
    return <Text>{name}</Text>;
  }, [name, value, trendingBrandsPeriodOptionList]);

  return (
    <Popover
      placement="bottom-start"
      flip
      isOpen={isOpen}
      onClose={handlePopoverClose}
      strategy={popoverStrategy}
      preventOverflow
    >
      <PopoverTrigger>
        <Button
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          fontSize="12px"
          minH="0"
          minWidth={minWidth}
          width={width || "auto"}
          height={height}
          variant="ghost"
          onClick={handleToggle}
          {...customStyle}
        >
          <Flex
            width="100%"
            flexDirection="row"
            alignItems="center"
            gap="8px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            position="relative"
            backgroundColor="inherit"
            pr="16px"
            height="100%"
          >
            {triggerComponent}

            {!isReadOnly && (
              <Box
                position="absolute"
                right="0"
                backgroundColor="inherit"
                paddingLeft="4px"
              >
                <Icon
                  as={SVG}
                  color="inherit"
                  src="/icons/icon_polygon_1.svg"
                  boxSize="8px"
                />
              </Box>
            )}

            {/* ✅ 옵션 clear를 원하면 사용 */}
            {isClearable && value && !isDisabled && !isReadOnly && (
              <Box
                position="absolute"
                right="8px"
                backgroundColor="inherit"
                paddingLeft="4px"
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(undefined);
                  handlePopoverClose();
                }}
                aria-label="clear"
              >
                <Icon as={SVG} src="/icons/icon_x_letter.svg" boxSize="10px" />
              </Box>
            )}
          </Flex>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        width={dropdownWidth ?? "auto"}
        overflow="hidden"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            handlePopoverClose();
          }
        }}
      >
        <PopoverHeader background="#F7F9FA" fontSize="12px">
          <Text>{title}</Text>
          <PopoverCloseButton />
        </PopoverHeader>

        <PopoverBody p="0">
          <List
            display="flex"
            flexDirection="column"
            fontSize="12px"
            maxH="172px"
            overflowY="auto"
          >
            {appSelectOptions.map((option) => (
              <AppSelectOption<TrendingPeriodValue>
                key={option.value}
                option={option}
                isActive={calcIsActive(option.value)}
                handleClick={(opt) => handleClick(opt as any)}
                isDisabled={isDisabled}
              />
            ))}
          </List>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
